import React from 'react';
import { Helmet } from 'react-helmet-async';

interface SEOHeadProps {
  title?: string;
  description?: string;
  keywords?: string;
  url?: string;
  image?: string;
  type?: 'website' | 'article';
  organizationData?: {
    name: string;
    foundingDate?: string;
    address?: string;
    email?: string;
    phone?: string;
    url?: string;
    logo?: string;
    socialMedia?: {
      facebook?: string;
      instagram?: string;
    };
  };
}

const SEOHead: React.FC<SEOHeadProps> = ({
  title = 'StimmArt - Pop-Chor Singgemeinschaft Bieber/Wiesen',
  description = 'StimmArt ist ein moderner Pop-Chor aus Bieber und Wiesen. Seit 2023 bringen wir Freude und zeitgemäße Musik in die Region.',
  keywords = 'StimmArt, Pop-Chor, Singgemeinschaft, Bieber, Wiesen, moderner Chor, Pop-Musik, Chorgesang, Gesang, Musik, Biebergemünd',
  url = 'https://stimmart-chor.de',
  image,
  type = 'website',
  organizationData
}) => {
  // Strukturierte Daten (JSON-LD) für Google
  const structuredData = organizationData ? {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: organizationData.name,
    url: organizationData.url || url,
    logo: organizationData.logo,
    foundingDate: organizationData.foundingDate,
    email: organizationData.email,
    telephone: organizationData.phone,
    address: organizationData.address ? {
      '@type': 'PostalAddress',
      addressLocality: 'Bieber',
      addressRegion: 'Hessen',
      addressCountry: 'DE'
    } : undefined,
    sameAs: [
      organizationData.socialMedia?.facebook,
      organizationData.socialMedia?.instagram
    ].filter(Boolean)
  } : null;

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      {image && <meta property="og:image" content={image} />}
      
      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      {image && <meta name="twitter:image" content={image} />}
      
      {/* Canonical URL */}
      <link rel="canonical" href={url} />
      
      {/* Strukturierte Daten (JSON-LD) */}
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData, null, 2)}
        </script>
      )}
    </Helmet>
  );
};

export default SEOHead;

